import { useInterval } from 'ahooks';
import React, { useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';

import { ThemeProvider } from '@coinflare/ui/theme';
import { I18NProvider } from '@coinflare/ui/translations';

import { NOTIFICATION_TYPES } from 'src/common/constants';
import { Notify } from 'src/components/core';
import i18next from 'src/config/i18n';
import { useAuth } from 'src/context/AuthContext';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import Routes from 'src/routing/Routes';
import * as globalActions from 'src/setup/redux/slices/globalSlice';
import * as userActions from 'src/setup/redux/slices/userSlice';
import { WebSocketProvider } from 'src/setup/socket/socketContext';

const App: React.FC = () => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();
    const { isAuthenticated } = useAuth();

    const { activeLanguage } = useAppSelector((state) => state.global);
    const { userFeaturesConfig } = useAppSelector((state) => state.user);

    const [mainComponentKey, setMainComponentKey] = useState(0);

    const getNotifications = useCallback(() => {
        if (isAuthenticated) {
            dispatch(globalActions.getNotifications());
        }
    }, [dispatch, isAuthenticated]);

    useEffect(() => {
        dispatch(globalActions.refreshCommonConfigFromStorage());
        dispatch(globalActions.fetchIndexConfig());
        dispatch(globalActions.fetchConfigV2());
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            dispatch(userActions.fetchUserBaseInfo());
            dispatch(userActions.fetchUserLevelInfo());
            dispatch(userActions.getUserAssets({ unit: 'USDT' }));
            dispatch(userActions.fetchUserVerifyInfo());
            dispatch(globalActions.getSettings());
            dispatch(globalActions.getPopupNotifications());
            dispatch(userActions.fetchUserFeaturesConfig());
            dispatch(userActions.fetchUserKycConfig());
            dispatch(userActions.fetchUserBonus());
        }
    }, [isAuthenticated]);

    useInterval(getNotifications, 7000);

    const notify = (title: string) => {
        Notify({
            type: NOTIFICATION_TYPES.INFO,
            title: t(title),
            iconColor: '#4282FF',
        });
    };

    useEffect(() => {
        function handleOnline() {
            setMainComponentKey(mainComponentKey + 1);
            setTimeout(() => {
                notify('online');
            }, 3000);
        }

        function handleOffline() {
            notify('offline');
        }

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    useEffect(() => {
        if (userFeaturesConfig?.symbols) {
            dispatch(globalActions.updateGlobalConfigSymbols(userFeaturesConfig?.symbols));
        }
    }, [userFeaturesConfig?.symbols]);

    return (
        <BrowserRouter basename="/trade" key={mainComponentKey}>
            <WebSocketProvider>
                <I18NProvider i18n={i18next}>
                    <ThemeProvider theme={'light'} key={activeLanguage}>
                        <Routes />
                    </ThemeProvider>
                </I18NProvider>
            </WebSocketProvider>
        </BrowserRouter>
    );
};

export { App };
